/**
 * The canonical way to talk to the Parrot backend: base-URL resolution, the
 * `x-auth-token` auth convention, JSON handling, and error normalization — in
 * one place, so apps stop hand-rolling (and drifting) their own `fetch`.
 *
 * It is deliberately small. It is NOT a data-fetching framework: caching,
 * retries, loading/error state, and deduplication are TanStack Query's job —
 * this is just the fetcher you hand to it.
 */

/** Production API host. Apps should pass their own env-specific `baseUrl`. */
export const DEFAULT_BASE_URL = 'https://prodapi.parrotanalytics.com';

/**
 * Reads the Parrot auth token from localStorage. `auth_token` is the canonical
 * key; `token` is a legacy fallback still present in some apps. Returns undefined
 * in non-browser (SSR/test) contexts, or when neither key is set — `null` from
 * `getItem` is normalized to `undefined` here (rule #18) so it never flows onward.
 */
export function defaultGetAuthToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return (
    window.localStorage.getItem('auth_token') ?? window.localStorage.getItem('token') ?? undefined
  );
}

export interface ApiClientOptions {
  /** Defaults to {@link DEFAULT_BASE_URL}. */
  baseUrl?: string;
  /** Override how the auth token is resolved. Defaults to {@link defaultGetAuthToken}. */
  getAuthToken?: () => string | undefined;
  /**
   * Called on any 401, just before the {@link ApiError} is thrown — the seam for
   * dropping a session the server has stopped accepting (`onUnauthorized:
   * session.clear`). The error still throws, so callers keep their own error handling.
   */
  onUnauthorized?: () => void;
}

export interface ApiClient {
  /** The general-purpose request. Use this for anything the verb helpers don't cover (FormData, custom methods). */
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
  get: <T>(path: string, init?: RequestInit) => Promise<T>;
  /** `body` is JSON-serialized. For non-JSON payloads use `request` directly. */
  post: <T>(path: string, body?: unknown, init?: RequestInit) => Promise<T>;
  put: <T>(path: string, body?: unknown, init?: RequestInit) => Promise<T>;
  patch: <T>(path: string, body?: unknown, init?: RequestInit) => Promise<T>;
  delete: <T>(path: string, init?: RequestInit) => Promise<T>;
}

/** Thrown on any non-2xx response. Carries `status` and the parsed `body` so callers can branch (e.g. 401 → re-auth). */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) return res.json();
  return res.text();
}

function errorMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string') {
    return body.error;
  }
  return `Request failed with status ${status}`;
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const {
    baseUrl = DEFAULT_BASE_URL,
    getAuthToken = defaultGetAuthToken,
    onUnauthorized,
  } = options;

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = getAuthToken();
    const res = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'x-auth-token': token } : {}),
        ...init.headers,
      },
    });

    const body = await parseBody(res);
    if (!res.ok) {
      if (res.status === 401) onUnauthorized?.();
      throw new ApiError(errorMessage(body, res.status), res.status, body);
    }
    // The one sanctioned `as` in this lib (rule #1 carve-out): a generic transport
    // cannot verify the runtime shape, so the single unavoidable assertion is
    // centralized here rather than scattered across call sites. Domain callers still
    // zod-parse where the shape matters (PATTERNS.md, data-fetching layer 2).
    return body as T;
  }

  function withBody(method: string) {
    return <T>(path: string, body?: unknown, init: RequestInit = {}): Promise<T> =>
      request<T>(path, {
        ...init,
        method,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
  }

  return {
    request,
    get: (path, init) => request(path, { ...init, method: 'GET' }),
    post: withBody('POST'),
    put: withBody('PUT'),
    patch: withBody('PATCH'),
    delete: (path, init) => request(path, { ...init, method: 'DELETE' }),
  };
}
